import { useEffect } from 'react';
import { analytics } from '../../firebase';
import { logEvent } from 'firebase/analytics';

export default function Legal() {
  const pathname = window.location.pathname;
  const isPrivacyOnly = pathname.includes('/privacy');

  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_title: isPrivacyOnly ? 'Privacy Policy - Tattva Dynamics' : 'Legal - Tattva Dynamics',
      page_location: window.location.href,
      page_path: window.location.pathname
    });
    window.scrollTo(0, 0);
  }, [isPrivacyOnly]);

  const accentColor = '#bf7039';
  const backgroundColor = '#f8f9fa';
  const darkTextColor = '#333333';
  const lightTextColor = '#666666';
  const borderColor = '#e9ecef';

  const sectionHeaderStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${borderColor}`,
    marginTop: '32px'
  };

  const subHeaderStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '12px',
    marginTop: '24px'
  };

  const cardStyle = {
    padding: '24px',
    backgroundColor: backgroundColor,
    borderRadius: '8px',
    border: `1px solid ${borderColor}`,
    marginBottom: '32px'
  };

  const headingStyle = {
    fontWeight: '300',
    color: '#1a1a1a'
  };

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: '#ffffff',
      color: darkTextColor,
      lineHeight: '1.6',
      fontSize: '16px'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: backgroundColor,
        padding: '60px 20px',
        textAlign: 'center',
        borderBottom: `1px solid ${borderColor}`
      }}>
        <img
          src="/logo.png"
          alt="Tattva Dynamics Logo"
          style={{
            width: '80px',
            height: '80px',
            marginBottom: '24px',
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        />
        <h1 style={{ ...headingStyle, fontSize: '2.5rem', margin: '0 0 8px 0' }}>
          {isPrivacyOnly ? 'Privacy Policy' : 'Master Terms & Legal'}
        </h1>
        <p style={{
          fontSize: '18px',
          color: accentColor,
          margin: '0 0 8px 0',
          fontWeight: '500'
        }}>
          Tattva Dynamics - Essence. Engineered.
        </p>
        <p style={{
          fontSize: '14px',
          color: lightTextColor,
          margin: '0'
        }}>
          Effective Date: August 15, 2025
        </p>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '48px 20px'
      }}>
        
        {!isPrivacyOnly && (
          <section id="terms">
            <h2 style={{ ...headingStyle, ...sectionHeaderStyle, fontSize: '1.75rem', marginTop: 0 }}>Terms of Service</h2>
            <p>Tattva Dynamics ("the Company") provides integrated software, hardware, and IT consulting services. By engaging with our website, purchasing our products, or using our applications, you agree to the following terms.</p>
            
            <h3 style={subHeaderStyle}>1. Product Usage & Safety</h3>
            <p>Our hardware and software products must be used in accordance with the provided documentation. For industrial or mission-critical solutions (such as DutyVigil), users must ensure compliance with local safety regulations and operational standards.</p>

            <h3 style={subHeaderStyle}>2. Software License</h3>
            <p>Unless otherwise stated, software provided by Tattva Dynamics is licensed, not sold. We grant a limited, non-transferable license for use. Reverse engineering, unauthorized distribution, or tampering with the software core is strictly prohibited.</p>

            <h3 style={subHeaderStyle}>3. Hardware Warranty & Liability</h3>
            <p>Hardware products are covered by limited warranties as specified at the time of purchase. The Company is not liable for damages resulting from unauthorized modifications, environmental factors exceeding specified limits, or improper handling.</p>

            <h3 style={subHeaderStyle}>4. Intellectual Property</h3>
            <p>All technologies, proprietary algorithms, designs, and brand assets are the sole property of Tattva Dynamics. Engagement with our services does not constitute a transfer of any intellectual property rights.</p>

            <h3 style={subHeaderStyle}>5. Governing Law</h3>
            <p>These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Pune, Maharashtra, India.</p>
          </section>
        )}

        <section id="privacy">
          <h2 style={{ ...headingStyle, ...sectionHeaderStyle, fontSize: '1.75rem', marginTop: isPrivacyOnly ? 0 : '64px' }}>Privacy Policy</h2>
          <p>Tattva Dynamics is committed to maintaining the highest standards of data integrity and transparency. This policy covers data handling across our software ecosystems and hardware interfaces.</p>

          <h3 style={subHeaderStyle}>1. Information Governance</h3>
          <p>We collect information to provide better services to all our users. This includes:</p>
          <ul style={{ paddingLeft: '20px' }}>
            <li><strong>Infrastructure Data:</strong> For hardware products, we may collect telemetry data related to performance, power consumption, and error logs to ensure operational stability.</li>
            <li><strong>User Identity:</strong> Information provided during account creation, product registration, or support inquiries.</li>
            <li><strong>Service Usage:</strong> Patterns of interaction with our cloud-based and local applications.</li>
          </ul>

          <h3 style={subHeaderStyle}>2. Data Security & Sovereignty</h3>
          <p>We implement enterprise-grade encryption and security protocols. For critical operations, we offer deployment options that allow users to maintain data residency within their own infrastructure.</p>

          <h3 style={subHeaderStyle}>3. Third-Party Disclosures</h3>
          <p>We do not sell user data. Data is shared with third-party partners (such as cloud service providers or logistics partners) only when essential for service delivery and under strict confidentiality agreements.</p>

          <h3 style={subHeaderStyle}>4. Compliance</h3>
          <p>We strive to comply with global data protection standards (including GDPR and regional IT acts) where applicable to our operations and user base.</p>
        </section>

        {/* Contact Section */}
        <div style={{ ...cardStyle, marginTop: '64px' }}>
          <h2 style={{ ...headingStyle, ...sectionHeaderStyle, borderBottom: 'none', marginBottom: '8px', marginTop: 0 }}>
            Contact & Support
          </h2>
          <p style={{ color: '#495057', margin: '0 0 16px 0' }}>
            For legal inquiries, data deletion requests, or technical documentation regarding our products:
          </p>
          <div>
            <strong style={{ color: '#1a1a1a' }}>Email:</strong>
            <a href="mailto:support@tattvadynamics.com" style={{ color: accentColor, textDecoration: 'none', marginLeft: '8px', fontWeight: '500' }}>
              support@tattvadynamics.com
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        textAlign: 'center',
        padding: '32px 20px',
        backgroundColor: backgroundColor,
        borderTop: `1px solid ${borderColor}`,
        color: lightTextColor,
        fontSize: '14px'
      }}>
        <p style={{ margin: '0' }}>
          © {new Date().getFullYear()} Tattva Dynamics. All rights reserved.
        </p>
      </div>
    </div>
  );
}
